import type { AnyObject } from "@entrilia/shared/types.ts";
import type {
  CashFlowSettingsConfiguration,
  CashFlowValue,
  DrillDownInfo,
  TabularDataCell,
  TabularRow,
} from "../../../../../../../api/types/svTypes.ts";
import { buildCashFlowRows, type CashFlowMode, type CashFlowRow } from "./cashFlowRows.ts";

export const CASH_FLOW_SPLIT_FIELD_ID = "__cashFlowSplit";

export interface CashFlowSeries {
  id: string;
  label: string;
  description?: string;
  cell: TabularDataCell | undefined;
}

export interface CashFlowAmountCell {
  amount?: number;
  formattedValue?: string;
  drillDownInfo?: DrillDownInfo;
}

export interface CashFlowMatrixRow {
  id: string;
  parentId?: string;
  date: string;
  calculationDate?: string;
  label: string;
  kind: CashFlowRow["kind"] | "Irr" | "SectionTotal";
  expandable: boolean;
  expanded?: boolean;
  dimensions?: Record<string, string>;
  cells: Record<string, CashFlowAmountCell>;
}

const isTabularDataCell = (value: unknown): value is TabularDataCell =>
  typeof value === "object" && value !== null && "value" in value;

export const getCashFlowTotalSeries = (grandTotals: AnyObject, measureId: string): CashFlowSeries | undefined => {
  const cell = grandTotals[measureId];
  return isTabularDataCell(cell) ? { id: "amount:total", label: "Total", cell } : undefined;
};

type CashFlowSections = CashFlowSettingsConfiguration["sections"];

const sectionLabels: Record<Exclude<CashFlowSections, "Combined">, [string, string]> = {
  ContributionsAndDistributions: ["Contributions", "Distributions"],
  InvestmentsAndProceeds: ["Investments", "Proceeds"],
};
const CASH_FLOW_LABEL = "Cash flow";

export const getCashFlowSeries = (rows: TabularRow[], measureId: string, amountLabel: string): CashFlowSeries[] =>
  rows.map((row) => {
    const splitValue = row.data[CASH_FLOW_SPLIT_FIELD_ID];
    return {
      id: `amount:${row.id}`,
      label: splitValue ? splitValue.formattedValue || "(Blanks)" : amountLabel,
      description: splitValue ? amountLabel : undefined,
      cell: row.data[measureId],
    };
  });

const getSummaryId = (kind: CashFlowRow["kind"], label: string, date: string): string =>
  `${kind}:${label}:${date.slice(0, 10)}`;
const compareRows = (left: CashFlowMatrixRow, right: CashFlowMatrixRow): number => left.date.localeCompare(right.date);
const getEntryKey = (parentId: string, row: CashFlowRow): string | undefined => {
  if (!row.entryNo) {
    return undefined;
  }
  const combined = row.drillDownInfo?.combined;
  const ledger = combined && "table" in combined ? combined.table : "";
  return `${parentId}/${row.date.slice(0, 10)}/${ledger}/${row.entryNo}`;
};

const getSignedPart = (value: CashFlowValue, inflow: boolean): CashFlowValue | undefined => {
  const transactions = value.transactions ?? [];
  if (transactions.length === 0) {
    return value.amount > 0 === inflow ? value : undefined;
  }
  const part = transactions.filter((transaction) => transaction.amount > 0 === inflow);
  if (part.length === 0) {
    return undefined;
  }
  return {
    ...value,
    amount: part.reduce((sum, transaction) => sum + transaction.amount, 0),
    amountFormatted: (inflow ? value.positiveAmountFormatted : value.negativeAmountFormatted) ?? value.amountFormatted,
    transactions: part,
    drillDownInfo:
      part.length === transactions.length
        ? value.drillDownInfo
        : inflow
          ? value.positiveDrillDownInfo
          : value.negativeDrillDownInfo,
  };
};

const getSignedParts = (values: CashFlowValue[], inflow: boolean): CashFlowValue[] =>
  values.flatMap((value) => {
    const part = value.isNav || value.isIrr ? undefined : getSignedPart(value, inflow);
    return part ? [part] : [];
  });

export const buildCashFlowMatrix = (
  series: CashFlowSeries[],
  mode: CashFlowMode,
  showZeroTotals: boolean,
  isExpanded: (id: string) => boolean,
  irrLabel: string,
  sections: CashFlowSections
): CashFlowMatrixRow[] => {
  const buildRows = (
    label: string,
    selectValues: (values: CashFlowValue[]) => CashFlowValue[]
  ): CashFlowMatrixRow[] => {
    const summaries = new Map<string, CashFlowMatrixRow>();
    const transactions: CashFlowMatrixRow[] = [];
    const transactionsByEntry = new Map<string, CashFlowMatrixRow>();
    for (const column of series) {
      const rows = buildCashFlowRows(selectValues(column.cell?.cashFlows ?? []), mode, label);
      for (const row of rows) {
        const cell: CashFlowAmountCell = {
          amount: row.amount,
          formattedValue: row.amountFormatted,
          drillDownInfo: row.drillDownInfo,
        };
        if (row.kind === "Transaction") {
          const parentId = getSummaryId("Total", label, row.calculationDate ?? "");
          const entryKey = getEntryKey(parentId, row);
          const sameEntry = entryKey === undefined ? undefined : transactionsByEntry.get(entryKey);
          if (sameEntry && !(column.id in sameEntry.cells)) {
            sameEntry.cells[column.id] = cell;
            continue;
          }
          const transaction = { ...row, id: `${column.id}/${label}/${row.id}`, parentId, cells: { [column.id]: cell } };
          transactions.push(transaction);
          if (entryKey !== undefined && !sameEntry) {
            transactionsByEntry.set(entryKey, transaction);
          }
        } else {
          const id = getSummaryId(row.kind, row.label, row.date);
          const existing = summaries.get(id);
          if (existing) {
            existing.cells[column.id] = cell;
            existing.expandable ||= row.expandable;
          } else {
            summaries.set(id, { ...row, id, cells: { [column.id]: cell } });
          }
        }
      }
    }
    const detailsByParent = new Map<string, CashFlowMatrixRow[]>();
    for (const row of transactions) {
      const children = detailsByParent.get(row.parentId ?? "") ?? [];
      children.push(row);
      detailsByParent.set(row.parentId ?? "", children);
    }
    if (mode === "Details") {
      return [...summaries.values(), ...transactions].sort(compareRows);
    }
    return [...summaries.values()].sort(compareRows).flatMap((row) => {
      const children = detailsByParent.get(row.id) ?? [];
      if (
        row.kind === "Total" &&
        !showZeroTotals &&
        Object.values(row.cells).every((cell) => cell.amount === 0) &&
        (mode !== "Both" || children.length === 0)
      ) {
        return [];
      }
      if (mode !== "Both") {
        return [row];
      }
      const summary = { ...row, expanded: isExpanded(row.id) };
      return summary.expanded ? [summary, ...children.sort(compareRows)] : [summary];
    });
  };
  const buildSectionRows = (label: string, inflow: boolean): CashFlowMatrixRow[] => [
    ...buildRows(label, (values) => getSignedParts(values, inflow)),
    {
      id: `SectionTotal:${label}`,
      kind: "SectionTotal",
      date: "",
      label: `Total ${label.toLowerCase()}`,
      expandable: false,
      cells: Object.fromEntries(
        series.map((column) => [
          column.id,
          {
            formattedValue: inflow
              ? column.cell?.positiveCashFlowTotalFormatted
              : column.cell?.negativeCashFlowTotalFormatted,
          },
        ])
      ),
    },
  ];
  const rows =
    sections === "Combined"
      ? buildRows(CASH_FLOW_LABEL, (values) => values)
      : [
          ...buildRows(CASH_FLOW_LABEL, (values) => values.filter((value) => value.isNav && value.isBeginningOfPeriod)),
          ...buildSectionRows(sectionLabels[sections][0], false),
          ...buildSectionRows(sectionLabels[sections][1], true),
          ...buildRows(CASH_FLOW_LABEL, (values) =>
            values.filter((value) => value.isNav && !value.isBeginningOfPeriod)
          ),
        ];
  rows.push({
    id: "irr",
    kind: "Irr",
    date: "",
    label: irrLabel,
    expandable: false,
    cells: Object.fromEntries(
      series.map((column) => [column.id, { formattedValue: column.cell?.formattedValue || "Not available" }])
    ),
  });
  return rows;
};
